import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, supabaseWithoutCSRF } from "../../../supabaseClient";
import { updateBowlerAverage } from "../../../utility/dataOperations/updateBowlerAverage";
import { sanitizeEmail } from "../../../utils/sanitization";
import { notify } from "../../../components/UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../../utility/errorSanitization";
import { saveEditTeamStartingLane } from "../../../utility/laneAssignmentApi";
import {
  replaceBowlerAveragePayload,
  teamEditAverageErrors,
  teamEditRpcMessage,
} from "./teamEditRpc";
import { applyBowlerTicketCounts } from "../../../utility/brackets/api";
import { updateTeamName } from "./teamName";

export { replaceBowlerAveragePayload, teamEditRpcMessage };

export const useTeamEditActions = (
  tournamentId,
  teamId,
  user,
  formData,
  setFormData,
  squadId
) => {
  const navigate = useNavigate();
  const [averageErrors, setAverageErrors] = useState({});

  const handleInputChange = useCallback(
    (bowlerIndex, field, value) => {
      // Apply email sanitization if the field is email
      const sanitizedValue = field === "email" ? sanitizeEmail(value) : value;

      const updatedBowlers = [...formData.bowlers];
      updatedBowlers[bowlerIndex] = {
        ...updatedBowlers[bowlerIndex],
        [field]: sanitizedValue,
      };

      // If hasAverage is being set to false, clear the average field
      if (field === "hasAverage" && !sanitizedValue) {
        updatedBowlers[bowlerIndex].average = null;
      }

      if (field === "average" || field === "hasAverage") {
        setAverageErrors((current) => {
          if (!current[bowlerIndex]) return current;
          const next = { ...current };
          delete next[bowlerIndex];
          return next;
        });
      }

      setFormData({ bowlers: updatedBowlers });
    },
    [formData.bowlers, setFormData]
  );

  const handlePaidEventChange = useCallback(
    (bowlerIndex, eventId) => {
      const updatedBowlers = [...formData.bowlers];
      const bowler = updatedBowlers[bowlerIndex];

      // Update selectedSidePots object
      const selectedSidePots = { ...(bowler.selectedSidePots || {}) };
      if (selectedSidePots[eventId]) {
        delete selectedSidePots[eventId];
      } else {
        selectedSidePots[eventId] = true;
      }

      // Convert selectedSidePots back to paidEvents array for backward compatibility
      const paidEvents = Object.keys(selectedSidePots).filter(
        (id) => selectedSidePots[id]
      );

      updatedBowlers[bowlerIndex] = {
        ...bowler,
        selectedSidePots,
        paidEvents,
      };
      setFormData({ bowlers: updatedBowlers });
    },
    [formData.bowlers, setFormData]
  );

  const handleTicketChange = useCallback(
    (bowlerIndex, productId, nextCount) => {
      const updatedBowlers = [...formData.bowlers];
      updatedBowlers[bowlerIndex] = {
        ...updatedBowlers[bowlerIndex],
        bracketTickets: {
          ...(updatedBowlers[bowlerIndex].bracketTickets || {}),
          [productId]: nextCount,
        },
      };
      setFormData({ bowlers: updatedBowlers });
    },
    [formData.bowlers, setFormData]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!teamId || !tournamentId) {
        console.error("Missing teamId or tournamentId");
        return;
      }

      const fieldErrors = teamEditAverageErrors(formData.bowlers);
      if (Object.keys(fieldErrors).length) {
        setAverageErrors(fieldErrors);
        return;
      }
      setAverageErrors({});

      try {
        // Update bowler details
        for (const bowler of formData.bowlers) {
          if (bowler.pendingReplacement || bowler.isNew) {
            continue;
          }
          // Update bowler basic information
          // eslint-disable-next-line no-unused-vars
          const { error: bowlerError } = await supabase
            .from("bowlers")
            .update({
              usbcID: bowler.usbcID,
            })
            .eq("id", bowler.id);

          // Update contact information in bowlersContact table
          if (bowler.phone || bowler.email) {
            // Soft delete existing contact info
            await supabase
              .from("bowlersContact")
              .update({
                deletedAt: new Date().toISOString(),
                deletedBy: user?.id,
              })
              .eq("bowlerID", bowler.id)
              .in("type", ["email", "phone"])
              .is("deletedAt", null);

            // Insert new contact info
            const contactEntries = [];
            if (bowler.email) {
              contactEntries.push({
                bowlerID: bowler.id,
                type: "email",
                value: bowler.email,
                isPrimary: true,
                createdBy: user?.id,
                created: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
            }
            if (bowler.phone) {
              contactEntries.push({
                bowlerID: bowler.id,
                type: "phone",
                value: bowler.phone,
                isPrimary: true,
                createdBy: user?.id,
                created: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
            }

            if (contactEntries.length > 0) {
              const { error: contactError } = await supabase
                .from("bowlersContact")
                .insert(contactEntries);

              if (contactError) {
                console.error("Error updating contact info:", contactError);
              }
            }
          }

          // Verified averages are locked by RLS; UI disables edits but Save
          // must not upsert or it fails with a misleading permission error.
          if (!bowler.isVerified) {
            let processedAverage = bowler.average;
            if (
              bowler.hasAverage === false ||
              processedAverage === "" ||
              processedAverage === null ||
              processedAverage === undefined
            ) {
              processedAverage = null;
            }

            const { error: averageError } = await updateBowlerAverage(
              bowler.id,
              tournamentId,
              processedAverage,
              bowler.hasAverage !== false
            );

            if (averageError) {
              const isRls =
                averageError.code === "42501" ||
                (typeof averageError.message === "string" &&
                  averageError.message.toLowerCase().includes("policy"));
              if (isRls) {
                throw Object.assign(
                  new Error(
                    "Cannot change verified bowler averages. Unverify averages first to edit them."
                  ),
                  { code: "VERIFIED_AVERAGE" }
                );
              }
              throw averageError;
            }
          }
        }

        // Update all event entries server-side (includes validation and atomic delete/insert)
        const bowlerEvents = formData.bowlers
          .map((bowler) => {
            const eventIds = bowler.selectedSidePots
              ? Object.keys(bowler.selectedSidePots).filter(
                  (id) => bowler.selectedSidePots[id]
                )
              : bowler.paidEvents || [];

            return {
              bowlerID: bowler.originalBowlerId || bowler.id,
              eventIds,
            };
          })
          .filter(
            (entry) =>
              entry.bowlerID && !String(entry.bowlerID).startsWith("new-")
          );

        const { error: updateEventsError } = await supabase.rpc(
          "update_team_event_entries",
          {
            p_team_id: teamId,
            p_bowler_events: bowlerEvents,
          }
        );

        if (updateEventsError) throw updateEventsError;

        if (squadId) {
          for (const bowler of formData.bowlers) {
            const bowlerId = bowler.originalBowlerId || bowler.id;
            if (!bowlerId || String(bowlerId).startsWith("new-")) continue;
            await applyBowlerTicketCounts({
              squadId,
              bowlerId,
              counts: bowler.bracketTickets,
            });
          }
        }

        notify.success("Team updated successfully!");
        navigate(-1); // Go back to previous page
      } catch (error) {
        console.error("Error updating team:", error);
        if (error?.code === "VERIFIED_AVERAGE") {
          notify.error(error.message);
          return;
        }
        const sanitizedError = sanitizeOperationError.update(
          error,
          "team",
          false
        );
        notify.error(sanitizedError);
      }
    },
    [tournamentId, teamId, user, formData.bowlers, navigate, squadId]
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const replaceBowler = useCallback(
    async (oldBowlerId, selectedBowler) => {
      const { error } = await supabaseWithoutCSRF.rpc("replace_team_bowler", {
        p_team_id: teamId,
        p_old_bowler_id: oldBowlerId,
        p_new_bowler: {
          id: selectedBowler.id,
          name: selectedBowler.name,
          usbcID: selectedBowler.usbcID || "",
          email: selectedBowler.email || "",
          phone: selectedBowler.phone || "",
          ...replaceBowlerAveragePayload(selectedBowler),
          isNew: Boolean(selectedBowler.isNew),
        },
      });
      if (error) throw error;
    },
    [teamId]
  );

  const moveSquad = useCallback(
    async (squadId) => {
      const { error } = await supabaseWithoutCSRF.rpc("move_team_squad", {
        p_team_id: teamId,
        p_squad_id: squadId,
      });
      if (error) throw error;
    },
    [teamId]
  );

  const saveStartingLane = useCallback(
    async (laneInput, currentTeam) => {
      return saveEditTeamStartingLane({
        team: currentTeam,
        laneInput,
        tournamentId,
      });
    },
    [tournamentId]
  );

  const saveTeamName = useCallback(
    (rawName) => updateTeamName(supabase, teamId, rawName),
    [teamId]
  );

  return {
    averageErrors,
    handleInputChange,
    handlePaidEventChange,
    handleTicketChange,
    handleSubmit,
    handleCancel,
    replaceBowler,
    moveSquad,
    saveStartingLane,
    saveTeamName,
  };
};
